import React, { useEffect, useState } from 'react'
import { Share } from 'react-native'

import { useToast } from '@zet/common/components/toast'
import { useNavigation } from '@zet/common/hooks/useNavigation'
import { useAddCustomerNextState } from '@zet/customer/src/api/hooks'
import {
  useSendConsentOtp,
  useShareReconsentGlobalOtpVerificationLink,
} from '@zet/customer/src/api/hooks/reconsent'
import useCustomerStore from '@zet/customer/src/store'
import useAddCustomerStore from '@zet/customer/src/store/addCustomer'
import { BottomSheet, colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack } from '@zet/design-system/layout'
import Pressable from '@zet/design-system/pressable'
import ArrowRight from '@zet/design-system/svg/ArrowRight'
import ErrorOutlined from '@zet/design-system/svg/ErrorOutlined'
import { B6, SH1, SH3 } from '@zet/design-system/typography'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'

const Reconsent = ({
  customerName,
  customerPhoneNumber,
  customerId,
  isReconsentFlow,
  subAgentId,
}) => {
  const { mutate: mutateWithExperiment } = useAddCustomerNextState()
  const [isBottomSheetVisible, setIsBottomSheetVisible] =
    useState<boolean>(false)
  const toast = useToast()
  const navigation = useNavigation()
  const setSelectedCustomer = useCustomerStore(
    (store) => store.setSelectedCustomer
  )
  const { setIsReconcentFlowOtpVerify } = useAddCustomerStore((state) => state)
  const { data: user } = useUserContext()

  const { data, mutate, isSuccess, isLoading } = useSendConsentOtp()
  const onReconsentShare = async (response) => {
    const result = await Share.share({
      message: i18n.t('reconsent.shareDescription', {
        link: response?.data?.link,
        name: user?.fullName,
      }),
    })
  }

  const {
    data: shareReconsentOtpLinkResponse,
    refetch: refetchReconsent,
    isLoading: refetchingReconsent,
    isFetching: isFetchingReconsent,
  } = useShareReconsentGlobalOtpVerificationLink({
    onSuccess: onReconsentShare,
    customerId,
    subAgentId,
  })

  useEffect(() => {
    if (isSuccess) {
      if (!data?.data?.lockedTill) {
        navigation.navigate('VerifyCustomerOtp', {
          customerId: customerId,
          isReconsentFlow: isReconsentFlow,
          subagentId: subAgentId,
        })
      } else if (data?.data?.reason === 'Too many attempts') {
        toast?.show({
          intent: 'error',
          heading: data?.data?.reason,
          hideAfter: 3000,
          description: data?.data?.lockedTill,
        })
      }
    }
  }, [isSuccess])

  const handlePress = () => {
    setIsBottomSheetVisible(false)
    const requestBodyData = {
      fullName: customerName,
      phoneNumber: customerPhoneNumber,
      screenName: 'SHOWN_PHONE_NUMBER_SCREEN',
      isVerifyOtpOnly: isReconsentFlow,
      isReconsentFlow: isReconsentFlow,
      subagentId: subAgentId,
    }
    setIsReconcentFlowOtpVerify(true)
    mutateWithExperiment(requestBodyData)
  }

  return (
    <Pressable
      style={{ backgroundColor: '#FFE6E0' }}
      className="rounded-[4] px-3 py-[12]"
      onPress={() => {
        setSelectedCustomer({
          name: customerName,
          phoneNumber: customerPhoneNumber,
        })
        setIsBottomSheetVisible((state) => !state)
      }}
      eventName="click_action"
      eventPayload={{
        item_name: 'brands_list_recommendation_unlock_new_earnings',
      }}
    >
      <BottomSheet
        visible={isBottomSheetVisible}
        disableBottomSheet={() => setIsBottomSheetVisible(false)}
        onDismiss={() => setIsBottomSheetVisible(false)}
        sheetName="reconsent_share"
      >
        <>
          <XStack className="border-grays-30 items-center justify-between border-b px-3 py-2">
            <XStack className="items-center">
              <Icon
                type="ErrorFilled"
                className="mr-3"
                color={zetColors.error[100]}
              />
              <SH1>{i18n.t('reconsent.update')}</SH1>
            </XStack>
            <Pressable onPress={() => setIsBottomSheetVisible(false)}>
              <Icon type="Close" />
            </Pressable>
          </XStack>
          <Stack className="border-grays-30 border-b px-3 py-[12]">
            <SH3 className="text-grays-60">
              {i18n.t('reconsent.subHeading')}
            </SH3>
          </Stack>
          <XStack className="justify-between px-3 py-[12]">
            <Button
              className="mr-3 flex-grow justify-center"
              textStyle="text-primary-100"
              outlined={true}
              onPress={refetchReconsent}
              eventName="click_action"
              eventPayload={{
                item_name: 'brands_list_recommendation_reconsent_share',
              }}
            >
              {i18n.t('reconsent.share')}
            </Button>
            <Button
              className="flex-grow justify-center"
              onPress={handlePress}
              loading={isLoading}
              disabled={isLoading}
              eventName="click_action"
              eventPayload={{
                item_name: 'brands_list_recommendation_reconsent_proceed',
              }}
            >
              {i18n.t('reconsent.start')}
            </Button>
          </XStack>
        </>
      </BottomSheet>
      <XStack className="items-center justify-between ">
        <XStack className="items-center">
          <Stack className="mr-2">
            {/* <Union /> */}
            <ErrorOutlined color={zetColors.error[100]} />
          </Stack>
          <Stack>
            <SH1 className="text-[##490A0A]">{i18n.t('reconsent.heading')}</SH1>
          </Stack>
        </XStack>
        <Stack>
          <ArrowRight color={zetColors.grays[80]} />
        </Stack>
      </XStack>
      <B6 className="mt-1" selectionColor={zetColors.grays[80]}>
        {i18n.t('reconsent.subHeading')}
      </B6>
    </Pressable>
  )
}

export default Reconsent
